import type { Statute } from '../hooks/useStatutes';
import { parseStatuteTextForReader } from './statuteFormat';
import { classifyStatute, statuteDisplayCategoryLabel } from './statuteTaxonomyFilter';

const SNIPPET_LEN = 220;

function parseStatuteContentObject(content: string | undefined): Record<string, unknown> | null {
  if (!content?.trim() || !content.trimStart().startsWith('{')) return null;
  try {
    return JSON.parse(content) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/** Card headline: canonical full title from v2 JSON metadata when present, else Firestore title. */
export function statuteCardFullTitle(statute: Statute): string {
  const raw = parseStatuteContentObject(typeof statute.content === 'string' ? statute.content : undefined);
  if (raw) {
    const meta = raw.metadata as Record<string, unknown> | undefined;
    if (meta && typeof meta.title === 'string' && meta.title.trim()) {
      return meta.title.trim();
    }
  }
  const t = statute.title?.trim();
  if (t) return t;
  return 'Untitled statute';
}

function truncate(s: string, n: number): string {
  const t = s.replace(/\s+/g, ' ').trim();
  if (t.length <= n) return t;
  return `${t.slice(0, n).trim()}…`;
}

/** Try to read a display snippet from stored JSON (preamble / metadata) without trusting description alone. */
export function statuteSnippetFromContentJson(content: string | undefined): string | null {
  const raw = parseStatuteContentObject(content);
  if (!raw) return null;
  const meta = raw.metadata as Record<string, unknown> | undefined;
  if (meta && typeof meta.shortTitle === 'string' && meta.shortTitle.trim()) {
    return truncate(meta.shortTitle, SNIPPET_LEN);
  }
  if (meta && typeof meta.title === 'string' && meta.title.trim()) {
    return truncate(meta.title, SNIPPET_LEN);
  }
  if (raw.schemaVersion === 'v2' && Array.isArray(raw.structure)) {
    const first = raw.structure[0] as Record<string, unknown> | undefined;
    if (first?.type === 'preamble' && typeof first.title === 'string' && first.title.trim()) {
      return truncate(first.title, SNIPPET_LEN);
    }
  }
  return null;
}

export function statuteCardSnippet(statute: Statute): string {
  const desc = statute.description?.trim();
  if (desc) return truncate(desc, SNIPPET_LEN);
  const fromJson = statuteSnippetFromContentJson(
    typeof statute.content === 'string' ? statute.content : undefined,
  );
  if (fromJson) return fromJson;
  const c = typeof statute.content === 'string' ? statute.content.trim() : '';
  if (!c) {
    return 'Open this statute to load provisions from storage.';
  }
  if (c.startsWith('{') || c.startsWith('[')) {
    return 'Legal reference — open to browse the table of contents.';
  }
  return truncate(c, SNIPPET_LEN);
}

export function statuteProvisionCountFromInlineContent(content: string | undefined): number | null {
  if (!content?.trim()) return null;
  const rows = parseStatuteTextForReader(content);
  const sectionOnly = rows.filter((row) => {
    const id = String(row.id || '');
    const label = String(row.label || '');
    return /^s-\d+[A-Za-z]*/i.test(id) || /^\s*Section\s+\d+[A-Za-z]*\b/i.test(label);
  });
  return sectionOnly.length > 0 ? sectionOnly.length : null;
}

export function statuteCardFooterMeta(statute: Statute): {
  jurisdiction: string;
  category: string;
  sectionCount: number | null;
} {
  const jurisdiction = statute.jurisdiction?.trim() || 'Pakistan';
  const category = statuteDisplayCategoryLabel(classifyStatute(statute));
  const sectionCount = statuteProvisionCountFromInlineContent(
    typeof statute.content === 'string' ? statute.content : undefined,
  );
  return { jurisdiction, category, sectionCount };
}

export function formatStatuteDetailFooter(
  statute: Statute & { createdAt?: { toDate?: () => Date }; updatedAt?: { toDate?: () => Date } },
  sectionCount: number,
  contentSample: string,
): string {
  const jurisdiction = statute.jurisdiction?.trim() || 'Pakistan';
  let year: string | null = null;
  try {
    const t = contentSample.trim();
    if (t.startsWith('{')) {
      const o = JSON.parse(t) as { metadata?: { year?: number } };
      if (o.metadata?.year != null) year = String(o.metadata.year);
    }
  } catch {
    /* ignore */
  }
  if (!year) {
    const m = statute.title?.match(/\b(18|19|20)\d{2}\b/);
    year = m ? m[0] : null;
  }
  const segs: string[] = [jurisdiction];
  if (year) segs.push(year);
  segs.push(`${sectionCount} section${sectionCount === 1 ? '' : 's'}`);

  const d =
    statute.updatedAt && typeof statute.updatedAt.toDate === 'function'
      ? statute.updatedAt.toDate()
      : statute.createdAt && typeof statute.createdAt.toDate === 'function'
        ? statute.createdAt.toDate()
        : null;
  if (d) {
    segs.push(
      `Updated ${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`,
    );
  }
  return segs.join(' • ');
}
